import { appSettings } from "core/config";
import { dateWrapper } from "core/lib";
import { PaymentAmount } from "core/stores/payment.store";
import { AmountToPay } from "core/types/business-details";
import BaseCefCommercialService from "./base.service";
import { CefVersion } from "./constants";
import { AmountWithCurrency } from "./types";
import { activityLogArea } from "../user-tracking-helper";

type AccountBalance = {
    dueOnNextIpDay: AmountWithCurrency,
    dueNow: AmountWithCurrency,
    balance: AmountWithCurrency
}

type Account = {
    accountBalance: AccountBalance,
    methodOfPayment: string,
    dueAmount: AmountWithCurrency,
    amountToPay: AmountWithCurrency,
    accountNumber: number,
    dueDate: Date,
    accountType: string
}

type CustomerAccount = {
    customerNumber: string,
    dueDate?: Date | string,
    totalAmountToPay: AmountWithCurrency
    accounts: Account[]
}

export type CustomerAccountsResponse = {
    customerAccounts: CustomerAccount
}

export class CustomerAccountsResponseWrapper {
    private _account: CustomerAccount;
    private _useBalance = appSettings.isEnabled.paymentsUseBalance;

    constructor(response: CustomerAccountsResponse) {
        this._account = response.customerAccounts;
    }

    get balance() {
        const data = this._account;

        const formatValue = (value: number) => {
            return !appSettings.isEnabled.paymentsShowCredit && value < 0
                ? 0
                : value;
        }

        if (!this._useBalance) {
            return formatValue(data.totalAmountToPay.value);
        }

        const amount = data.accounts?.length ?? false
            ? data.accounts[0].accountBalance.balance.value
            : data.totalAmountToPay.value;

        return formatValue(amount);
    }

    get currencyCode() {
        return this._account.totalAmountToPay.currencyCode;
    }

    get currencySymbol() {
        return this._account.totalAmountToPay.currencySymbol;
    }

    get dueDate() {
        let dueDate: string | Date | undefined = this._account.dueDate;
        const dw = dateWrapper(dueDate);

        return dw.isValid ? dw.date : undefined;
    }

    toAmountToPay() {
        const amount: AmountToPay = {
            value: this.balance,
            currencyCode: this.currencyCode,
            currencySymbol: this.currencySymbol,
            dueDate: this.dueDate
        };

        return amount;
    }

    toPaymentAmount() {
        const amount: PaymentAmount = {
            amount: this.balance,
            currencyCode: this.currencyCode ?? "",
            currencySymbol: this.currencySymbol ?? "",
            dueDate: this.dueDate
        };

        return amount;
    }
}

class AmountToPayApi extends BaseCefCommercialService {
    constructor() {
        super(activityLogArea.amountToPay);
        this.setBaseUrl(`CEFCommAmountToPay/${CefVersion}`);
    }

    getCustomerAccounts() {
        this.userTracking.read();

        const path = `/customers/${this.customerNumber}/accounts`;
        return this.createPromiseRequest(path).get<CustomerAccountsResponse>();
    }
}

export default AmountToPayApi;
